import type {
  Block, Cell, ChipCell, Column, NoteBlock, SubCell, TableBlock, Tone,
} from '@/kova/spec/types'

import { ApiError } from '@/lib/api/errors'

import type { DataSource, ScreenData, ScreenLoader } from './provenance'
import { readAtStamp } from './provenance'

/**
 * Recognition › Payer catalog (`rc-catalog`), read from
 * `GET /v1/admin/catalog/entries` and
 * `GET /v1/admin/catalog/entries/{entryKey}/versions` (go-wallet#300, handler
 * `internal/server/admin_catalog.go`).
 *
 * # Invariant 9 is what this screen is FOR
 *
 * Catalog rows are versioned with `effective_from`/`effective_to` and never
 * updated in place — `EditEntry` closes the live row and inserts `version+1`,
 * `RetireEntry` closes it and sets `active = FALSE`, and nothing ever deletes.
 * The whole point of paying that cost is the acceptance test: given an entry key
 * and a date, an operator can say which version was in force then, six months
 * later, without guessing.
 *
 * So the question this file has to keep answering is not "can we draw the list"
 * — it is "does what we drew let somebody answer that". Two things follow, and
 * they are the reason this screen makes two reads:
 *
 *   - **The list cannot answer it.** `ListEntries` is `DISTINCT ON (entry_key)
 *     ORDER BY version DESC`: one row per key, the version in force now (or the
 *     last one, for a retired entry). A version that governed in April is not in
 *     that payload at all. A screen that showed only the list and said nothing
 *     would be quietly claiming the timeline is what you see.
 *   - **The history read answers it for one entry.** So it is made, its subject
 *     is named on screen, and the entries whose timelines are NOT shown are
 *     named as not shown. One timeline rendered without that sentence reads as
 *     the catalog's timeline.
 *
 * # Retired must be legible without reading timestamps
 *
 * The backend went to the trouble of sending `current` and `status` precisely so
 * a client would not re-derive "is this in force" from `effective_to`, and get
 * it wrong. Both are used, and they are cross-checked against each other rather
 * than trusted singly: if a row says `status: "active"` while `current` is
 * false, neither is stated as fact.
 *
 * `status` carries a trap the list does not have and the history does. It is
 * computed per ROW from `Current`, so on a version history every superseded
 * version of a perfectly healthy entry arrives as `"retired"`. That word is
 * right about the entry on the list and wrong about a version in the history, so
 * the history table does not render it: it reads `current` and `active` together
 * instead, which is the only pair that separates "replaced by v5" from "the
 * whole entry was retired".
 *
 * # Nothing may be said in the present tense about a version that is not in force
 *
 * The same trap in a second place, and a louder one. Both reads carry
 * `invalid_patterns`, and both carry versions that are NOT governing — the
 * history renders every one of them, and the list renders a retired entry's
 * closed last version. "This entry is live and can never match" is true only of
 * a version with `current = true`; said over a superseded one it turns a pattern
 * somebody fixed three edits ago into a red alarm about a healthy entry. So the
 * standing of the version is carried alongside every finding and decides which
 * sentence is allowed.
 *
 * # The `ver` block is deliberately not emitted
 *
 * The design draws `table` → `ver`. Only tables are emitted, and the version
 * history takes the `ver` block's place.
 *
 * `Ver` is a WRITE composer: its diff column is headed "Proposed", its date
 * field is labelled "earliest permitted", and it renders a live "Save as new
 * version" button that `Console.tsx` enables for any allowed role on a primary
 * cell. Nothing here is wired to a write, and its substance — the authoring
 * operator per version, the diff against live, the notice-period gate, the blast
 * radius — is answered by neither read. Rendering it would put an enabled button
 * that does nothing above four invented facts, which is the same failure
 * `candidates.ts` refuses for the promotion composer. The read-only half of it,
 * the version rail, is real and is rendered as a table.
 */

// ---------------------------------------------------------------------------
// The wire shape — mirrors internal/server/admin_catalog.go json tags
// ---------------------------------------------------------------------------

/**
 * One version of one entry.
 *
 * Field names are the Go tags verbatim. Three are load-bearing beyond naming:
 *
 * - `currency` is ALWAYS present and `""` is a real domain state — an entry that
 *   matches regardless of currency. The backend says so explicitly: a
 *   currency-less amount is forbidden (invariant 4), a currency-less matching
 *   rule is not, and omitting the key would make the two look alike. So it is
 *   never filled in and never read as "we did not record one".
 * - `bic` is `omitempty`, so an entry with no BIC has no key at all. Absent here
 *   means empty, not unread.
 * - `effective_to` is a POINTER on the Go side and therefore `null` rather than
 *   absent when a version is still in force — the comment on the struct says it
 *   exists so a client does not render a zero time as the year 1.
 */
export interface CatalogEntryView {
  readonly id: number
  readonly version: number
  readonly entry_key: string
  readonly platform_id: string
  readonly currency: string
  readonly ibans: readonly string[]
  readonly bic?: string
  readonly name_aliases: readonly string[]
  readonly remittance_patterns: readonly string[]
  readonly active: boolean
  readonly effective_from: string
  readonly effective_to: string | null
}

/**
 * A version plus the facts about the ENTRY no single version carries.
 *
 * `current`, `status` and `versions` are added by `listItemView` and are present
 * on the history rows too, because both endpoints render through the same
 * function. `status` means something different in each — see the file header.
 */
export interface CatalogListItemView extends CatalogEntryView {
  /** `effective_to IS NULL`. The version in force, which is not the same fact as being newest. */
  readonly current: boolean
  /** `active | retired`, per ENTRY on the list and per VERSION on the history. */
  readonly status: string
  /** How many rows share this entry_key. */
  readonly versions: number
  /**
   * Stored remittance patterns that will not compile. `omitempty`, so absent
   * means the backend compiled every one of them — which is a fact it verified,
   * not one this console is assuming.
   */
  readonly invalid_patterns?: readonly string[]
}

export interface CatalogListResponse {
  readonly entries: readonly CatalogListItemView[]
  /** Echoed, capped at `adminops.MaxCatalogPage`. There is no total. */
  readonly limit: number
  readonly offset: number
}

export interface CatalogVersionsResponse {
  readonly entry_key: string
  /** Newest first. `200` with `[]` for a key with no versions, never `404`. */
  readonly versions: readonly CatalogListItemView[]
}

export const CATALOG_ENTRIES_PATH = '/catalog/entries'

/** Entry keys are operator-authored text, so the segment is encoded. */
export function catalogVersionsPath(entryKey: string): string {
  return `${CATALOG_ENTRIES_PATH}/${encodeURIComponent(entryKey)}/versions`
}

const SOURCE = 'postgres · payer_catalog_entries (GET /v1/admin/catalog/entries)'

// ---------------------------------------------------------------------------
// Reading the wire, fail-closed
// ---------------------------------------------------------------------------

/** A string the backend actually sent, or nothing. Never a stand-in. */
function text(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

/** A boolean the backend actually sent. Anything else is "it did not say". */
function boolOrNull(value: unknown): boolean | null {
  return typeof value === 'boolean' ? value : null
}

/**
 * An integer we can state exactly, or nothing.
 *
 * `JSON.parse` hands back a double for a Go `int64`, so `id` past 2^53 has
 * already lost its last digits before it reaches here. On this screen that id is
 * the thing a decision row snapshots (invariant 9): an operator holding a
 * `decision_ref` matches its entry id and version against these rows to
 * reconstruct why a credit was tagged. An id wrong in its last places matches
 * the wrong version and reconstructs the wrong answer, confidently. `null`
 * routes it into the unreadable path instead.
 */
function exactInteger(value: unknown): number | null {
  if (typeof value !== 'number') return null
  return Number.isSafeInteger(value) ? value : null
}

/**
 * Go's zero `time.Time`, which `effective_from` can genuinely carry.
 *
 * It is a plain `time.Time`, not a pointer, so an unset one serialises as
 * `0001-01-01T00:00:00Z` and parses perfectly. It is the one absence that cannot
 * be caught by checking for `undefined`, and on this screen it would render an
 * effective window that began two thousand years ago.
 */
function isZeroInstant(at: Date): boolean {
  return at.getUTCFullYear() <= 1
}

/**
 * An RFC-3339 instant in the format the design draws timestamps in.
 *
 * Minute precision rather than the corpus's date-only `2026-05-01 → open`, and
 * deliberately: the acceptance test is "which version was in force on this
 * date", and two edits to the same entry on the same day are ordinary. A
 * date-only range makes the two versions that changed hands at 09:14 look like
 * they governed the same day, which is the one question this screen exists to
 * answer.
 *
 * An unparseable instant comes back verbatim rather than invented — an ugly
 * stamp is recoverable, a confidently wrong effective window is not.
 */
function stamp(value: unknown): string {
  const iso = text(value)
  if (iso === '') return ''
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return iso
  return isZeroInstant(at) ? '' : readAtStamp(at)
}

interface StringList {
  readonly items: readonly string[]
  readonly problem: string | null
}

/**
 * A list of strings the backend always sends, even when empty.
 *
 * `nonNil` in the handler renders an empty slice as `[]` and never as `null`,
 * with a comment saying a console that has to handle both has a bug waiting in
 * it. So an ABSENT list here is shape drift and is reported as such, rather than
 * silently read as an entry with no IBANs — which on this screen is the
 * difference between "matches nothing" and "we could not tell".
 */
function stringList(value: unknown, field: string): StringList {
  if (value === undefined || value === null) {
    return {
      items: [],
      problem: `${field} was not sent at all; the backend renders an empty list as [] and never omits it, so this is not an empty set`,
    }
  }
  if (!Array.isArray(value)) {
    return { items: [], problem: `${field} did not arrive as a list, so its contents are unknown` }
  }
  const items = value.filter((v): v is string => typeof v === 'string')
  if (items.length !== value.length) {
    return {
      items,
      problem: `${String(value.length - items.length)} item(s) in ${field} did not arrive as text and are not shown`,
    }
  }
  return { items, problem: null }
}

/** `invalid_patterns` is `omitempty`, so absent is the normal, healthy case. */
function optionalStringList(value: unknown, field: string): StringList {
  if (value === undefined) return { items: [], problem: null }
  return stringList(value, field)
}

// ---------------------------------------------------------------------------
// Cells
// ---------------------------------------------------------------------------

/**
 * The entry's currency scope.
 *
 * Empty is deliberate and documented — "the same platform pays EUR, GBP and USD
 * legs from different entities, and a few entries want to match regardless". It
 * is labelled as the domain state it is, never as a missing field and never
 * back-filled to EUR, which is invariant 4 broken in the cheapest place to break
 * it.
 */
function currencyCell(raw: unknown): SubCell {
  if (typeof raw !== 'string') {
    return ['not stated', 'the entry sent no currency field at all — this is not an any-currency entry']
  }
  if (raw === '') return ['any', 'scoped to no currency — deliberate, and it matches every leg']
  // Rendered verbatim and flagged rather than corrected: a code this console
  // does not recognise still identifies the scope to whoever wrote it, and an
  // ISO-4217 alphabetic code is three uppercase letters.
  return /^[A-Z]{3}$/.test(raw) ? [raw] : [raw, 'not an ISO-4217 code shape']
}

/**
 * What this entry matches on, from the fields that actually carry values.
 *
 * Not the corpus's `recognition_signal`, which is a single classified value no
 * endpoint answers for. This is a statement about the payload — which of the
 * four signal fields is populated — so it is named for what it is.
 */
function signalsText(
  ibans: readonly string[], bic: string,
  aliases: readonly string[], patterns: readonly string[],
): string {
  const parts: string[] = []
  if (ibans.length > 0) parts.push('debtor_iban')
  if (bic !== '') parts.push('bic')
  if (aliases.length > 0) parts.push('normalized_name')
  if (patterns.length > 0) parts.push('remittance_pattern')
  return parts.length === 0 ? 'none' : parts.join(' · ')
}

/** The payer as it is written in the entry, plus what else is behind it. */
function identityCell(
  ibans: readonly string[], bic: string,
  aliases: readonly string[], patterns: readonly string[],
): SubCell {
  const [firstIban] = ibans
  const [firstAlias] = aliases
  const [firstPattern] = patterns

  const head = firstIban ?? (bic === '' ? undefined : bic) ?? firstAlias ?? firstPattern
  if (head === undefined) {
    return [
      'no identifying signal',
      'no IBAN, BIC, alias or pattern — this entry carries nothing to match on and can never tag a credit',
    ]
  }

  const counts: string[] = []
  if (ibans.length > 0) counts.push(`${String(ibans.length)} IBAN(s)`)
  if (bic !== '') counts.push(`BIC ${bic}`)
  if (aliases.length > 0) counts.push(`${String(aliases.length)} alias(es)`)
  if (patterns.length > 0) counts.push(`${String(patterns.length)} pattern(s)`)
  return [head, counts.join(' · ')]
}

interface Chipped {
  readonly cell: ChipCell
  readonly problem: string | null
}

/**
 * Whether the version a row describes is the one governing right now.
 *
 * Three states and not two, because `current` can fail to arrive. A row that
 * did not say is NOT a closed row — it is a row whose standing is unknown, and
 * the difference decides whether a broken pattern is failing to match today or
 * stopped mattering when the next version opened.
 */
type Standing = 'in-force' | 'closed' | 'unknown'

function standingOf(current: boolean | null): Standing {
  if (current === null) return 'unknown'
  return current ? 'in-force' : 'closed'
}

/**
 * Whether this version's remittance patterns compile.
 *
 * `invalid_patterns` is the backend reporting the result of its own
 * `CompilePattern` call, so "they compile" is verified rather than assumed —
 * this is the one green chip on the screen that is allowed to be green.
 *
 * # "Inert" is a claim about the present, so only the version in force may make it
 *
 * A non-empty `invalid_patterns` on the version IN FORCE is the loudest thing
 * this screen can say: that entry loads, never matches, and nothing else in the
 * system would tell anybody.
 *
 * The same list on a version that is not in force says something much smaller.
 * A superseded version stopped matching the instant v(n+1) opened, and a
 * retired entry's last version is not matching either — both arrive here,
 * because the history renders every version and the list renders a retired
 * entry's closed last one. Calling those "inert" turns a pattern somebody
 * already fixed into a standing red alarm about a healthy entry, which is the
 * same overstatement in miniature that badging this screen `live` would be.
 */
function patternsChip(
  patterns: StringList, invalid: StringList, who: string, standing: Standing,
): Chipped {
  if (invalid.problem !== null) {
    return { cell: ['unreadable', 'r'], problem: `${who}: ${invalid.problem}` }
  }
  if (invalid.items.length > 0) {
    const which = invalid.items.map((p) => JSON.stringify(p)).join(', ')
    if (standing === 'in-force') {
      return {
        cell: [`inert · ${String(invalid.items.length)} bad`, 'r'],
        problem: `${who}: remittance pattern(s) ${which} will not compile, so this entry is live and can never match on them`,
      }
    }
    return {
      cell: [`${String(invalid.items.length)} bad`, 'a'],
      problem: standing === 'closed'
        ? `${who}: remittance pattern(s) ${which} will not compile, in a version that is not the one in force — nothing is matching on them now`
        : `${who}: remittance pattern(s) ${which} will not compile, and the row does not say whether this version is in force, so whether they are failing to match today is not established`,
    }
  }
  if (patterns.problem !== null) {
    return { cell: ['unreadable', 'r'], problem: `${who}: ${patterns.problem}` }
  }
  return patterns.items.length === 0
    ? { cell: ['no patterns', 'n'], problem: null }
    : { cell: [`${String(patterns.items.length)} compile`, 'g'], problem: null }
}

/**
 * The entry's status, as the backend named it, cross-checked against `current`.
 *
 * `active` and `retired` are the closed set `listItemView` writes. An unknown
 * one resolves to LESS confidence, never more — the same posture `availabilityOf`
 * takes on the dashboard. A fourth status added upstream and defaulted to
 * "active" here would show an operator a live entry that is not one.
 *
 * The cross-check exists because the two fields are two renderings of one fact
 * and a disagreement means the fact is not readable. Picking a side would state
 * as certain the very thing that is in doubt.
 */
function statusChip(rawStatus: unknown, current: boolean | null, who: string): Chipped {
  const status = text(rawStatus)

  if (current === null) {
    return {
      cell: ['unclear', 'r'],
      problem: `${who}: the row does not say whether it is the version in force, so "${status === '' ? 'no status' : status}" cannot be confirmed`,
    }
  }
  if (status === 'active' || status === 'retired') {
    const agrees = (status === 'active') === current
    if (!agrees) {
      return {
        cell: ['contradictory', 'r'],
        problem: `${who}: status is "${status}" while current is ${String(current)}; the two are renderings of the same fact and disagree, so neither is stated`,
      }
    }
    // Retired is neutral, not critical. Retiring an entry is an ordinary,
    // intended act — a console that paints it red teaches operators that red
    // means nothing, which is the tone budget the inert-pattern chip needs.
    return {
      cell: status === 'active' ? ['active', 'g'] : ['retired', 'n'],
      problem: null,
    }
  }
  return {
    cell: ['unclear', 'r'],
    problem: status === ''
      ? `${who}: no status was sent, so whether the entry is still in force is not stated here`
      : `${who}: status "${status}" is not one this console knows, and an unrecognised status is not read as active`,
  }
}

/**
 * A history row's state, from `current` and `active` together.
 *
 * The pair is the only thing that separates the two ways a version stops
 * governing, and the distinction matters: `EditEntry` closes the old row and
 * leaves `active = TRUE` on it, while `RetireEntry` closes the live row and sets
 * `active = FALSE`. So a closed-and-active version was SUPERSEDED by the next
 * one and its entry is alive; a closed-and-inactive version is the last word on
 * an entry somebody retired.
 *
 * `status` is not used here at all. It is computed per row from `current`, so
 * every superseded version of a healthy entry arrives labelled `"retired"` —
 * true of the row, false of the entry, and read as the entry.
 */
function versionStateChip(current: boolean | null, active: boolean | null, who: string): Chipped {
  if (current === null) {
    return { cell: ['unclear', 'r'], problem: `${who}: the row does not say whether it is the version in force` }
  }
  if (current) return { cell: ['live', 'g'], problem: null }
  if (active === null) {
    return {
      cell: ['closed · unclear', 'a'],
      problem: `${who}: this version is closed and does not say whether its entry was retired or the version merely superseded`,
    }
  }
  return active
    ? { cell: ['superseded', 'n'], problem: null }
    : { cell: ['retired', 'n'], problem: null }
}

interface EffectiveWindow {
  readonly text: string
  readonly problems: readonly string[]
}

/**
 * The effective window, which is the whole of invariant 9's acceptance test.
 *
 * `current` is authoritative about openness because the backend derives it from
 * `effective_to IS NULL` server-side. "open" is never said on the strength of a
 * missing field alone: an `effective_to` that arrived and could not be read is a
 * window whose end is unknown, and calling that open would claim this version
 * governs right now.
 */
function effectiveWindow(row: Partial<CatalogListItemView>, current: boolean | null, who: string): EffectiveWindow {
  const problems: string[] = []

  const from = stamp(row.effective_from)
  if (from === '') {
    problems.push(`${who}: no readable effective_from, so when this version began governing is unknown`)
  }
  const start = from === '' ? 'start not stated' : from

  const rawTo: unknown = row.effective_to
  const sentTo = rawTo !== undefined && rawTo !== null && rawTo !== ''
  const to = sentTo ? stamp(rawTo) : ''

  if (current === null) {
    problems.push(`${who}: the row does not say whether this version is in force`)
    return { text: `${start} → ${to === '' ? 'not stated' : to}`, problems }
  }

  if (current) {
    if (sentTo) {
      problems.push(
        `${who}: the row claims to be the version in force and also carries an effective_to, so the end of the window is not stated`,
      )
      return { text: `${start} → contradictory`, problems }
    }
    return { text: `${start} → open`, problems }
  }

  if (!sentTo) {
    problems.push(`${who}: this version is not in force and carries no effective_to, so when it stopped governing is unknown`)
    return { text: `${start} → closed, end not stated`, problems }
  }
  if (to === '') {
    problems.push(`${who}: the effective_to could not be read as an instant, so when this version stopped governing is unknown`)
    return { text: `${start} → closed, end not stated`, problems }
  }
  return { text: `${start} → ${to}`, problems }
}

// ---------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------

/**
 * One entry key that stores a pattern the backend could not compile, and
 * whether the version storing it is the one governing.
 *
 * The standing travels with the key because the two produce different notes.
 * Folding them into one list of keys is what let a broken pattern in a
 * superseded version raise "this entry is live and can never match" about an
 * entry whose live version was fixed three edits ago.
 */
interface BadPattern {
  readonly key: string
  readonly standing: Standing
}

interface RowRead {
  readonly cells: readonly Cell[]
  readonly withheld: readonly string[]
  /** Patterns that will not compile, for the standing notes. */
  readonly bad: readonly BadPattern[]
}

/** The common reading of one row, shared by the list and the history. */
interface RowFacts {
  readonly who: string
  readonly key: string
  readonly id: number | null
  readonly version: number | null
  readonly current: boolean | null
  readonly standing: Standing
  readonly active: boolean | null
  readonly ibans: readonly string[]
  readonly bic: string
  readonly aliases: readonly string[]
  readonly patterns: StringList
  readonly invalid: StringList
  readonly withheld: readonly string[]
}

function readFacts(row: Partial<CatalogListItemView>): RowFacts {
  const withheld: string[] = []

  const key = text(row.entry_key)
  const id = exactInteger(row.id)
  const version = exactInteger(row.version)

  // How the row is referred to in the withheld list. The entry key first,
  // because it is what the history endpoint takes and what an operator can find
  // on screen — the id is the field most likely to be the thing that failed.
  const who = key !== ''
    ? (version === null ? `entry "${key}"` : `entry "${key}" v${String(version)}`)
    : id === null ? 'an unidentified row' : `row id ${String(id)}`

  if (key === '') {
    withheld.push(
      `${who}: no entry_key, so this row cannot be looked up and its version history cannot be requested`,
    )
  }
  if (id === null) {
    withheld.push(
      `${who}: the row id could not be stated exactly, so it is not shown — a decision snapshots this id and a copy wrong in its last digits resolves to a different version`,
    )
  }
  if (version === null) {
    withheld.push(`${who}: the version number could not be stated, so this row cannot be placed in the entry's timeline`)
  }

  const ibans = stringList(row.ibans, 'ibans')
  const aliases = stringList(row.name_aliases, 'name_aliases')
  const patterns = stringList(row.remittance_patterns, 'remittance_patterns')
  for (const problem of [ibans.problem, aliases.problem, patterns.problem]) {
    if (problem !== null) withheld.push(`${who}: ${problem}`)
  }

  const current = boolOrNull(row.current)

  return {
    who,
    key,
    id,
    version,
    current,
    standing: standingOf(current),
    active: boolOrNull(row.active),
    ibans: ibans.items,
    // Absent is the normal case — `bic` is omitempty — so a missing key is an
    // entry with no BIC and nothing is withheld for it.
    bic: text(row.bic),
    aliases: aliases.items,
    patterns,
    invalid: optionalStringList(row.invalid_patterns, 'invalid_patterns'),
    withheld,
  }
}

const UNREADABLE_LIST_ROW: readonly Cell[] = [
  '—', '—', '—', ['unreadable row', 'the catalog returned something that is not an entry'],
  ['—'], '—', '—', '—', ['unreadable', 'r'], ['unreadable', 'r'],
]

const LIST_COLUMNS: readonly Column[] = [
  ['ID', 'm', '84px'],
  ['Entry key', 'm', 'minmax(170px,1.2fr)'],
  ['Platform', 'x', 'minmax(140px,1fr)'],
  ['Payer identity', 's', 'minmax(200px,1.4fr)'],
  ['Cur', 's', '96px'],
  ['Signals carried', 'x', 'minmax(160px,1.1fr)'],
  ['Version', 'm', '104px'],
  ['Effective (UTC)', 'm', 'minmax(200px,1.4fr)'],
  ['Status', 'c', '112px'],
  ['Patterns', 'c', '124px'],
]

/**
 * One entry, as a list row.
 *
 * Takes `unknown` because `Array.isArray` says nothing about the elements. A row
 * that is not an object is still rendered — as an unreadable row — because
 * dropping it shortens a catalog an operator is checking for duplicates and
 * tells them nothing about why.
 */
function readListRow(raw: unknown): RowRead {
  if (typeof raw !== 'object' || raw === null) {
    return {
      cells: UNREADABLE_LIST_ROW,
      withheld: ['a row that did not arrive as a catalog entry object'],
      bad: [],
    }
  }

  const row = raw as Partial<CatalogListItemView>
  const f = readFacts(row)
  const withheld = [...f.withheld]

  const status = statusChip(row.status, f.current, f.who)
  if (status.problem !== null) withheld.push(status.problem)

  const patterns = patternsChip(f.patterns, f.invalid, f.who, f.standing)
  if (patterns.problem !== null) withheld.push(patterns.problem)

  const window = effectiveWindow(row, f.current, f.who)
  withheld.push(...window.problems)

  const count = exactInteger(row.versions)
  if (count === null) {
    // Never a 1 here. "One version" is a claim about the entry's history —
    // that nothing preceded what is on screen — and this is a claim about the
    // read.
    withheld.push(`${f.who}: the number of versions could not be stated, so it is not known whether anything preceded this one`)
  }
  const versionCell = f.version === null
    ? '—'
    : count === null ? `v${String(f.version)}` : `v${String(f.version)} of ${String(count)}`

  return {
    cells: [
      f.id === null ? '—' : String(f.id),
      f.key === '' ? '—' : f.key,
      text(row.platform_id) === '' ? '—' : text(row.platform_id),
      identityCell(f.ibans, f.bic, f.aliases, f.patterns.items),
      currencyCell(row.currency),
      signalsText(f.ibans, f.bic, f.aliases, f.patterns.items),
      versionCell,
      window.text,
      status.cell,
      patterns.cell,
    ],
    withheld,
    bad: badPatternsOf(f),
  }
}

/**
 * Whether this row contributes to a standing pattern note.
 *
 * Gated on `invalid.problem === null` as well as on the list being non-empty:
 * a partly-unreadable `invalid_patterns` already renders the chip as
 * `unreadable` and is already withheld, and naming the entry in a note that
 * asserts which patterns are broken would state as known the thing that could
 * not be read.
 */
function badPatternsOf(f: RowFacts): readonly BadPattern[] {
  if (f.invalid.problem !== null || f.invalid.items.length === 0 || f.key === '') return []
  return [{ key: f.key, standing: f.standing }]
}

const UNREADABLE_VERSION_ROW: readonly Cell[] = [
  '—', '—', '—', ['unreadable', 'r'], '—',
  ['unreadable row', 'the history returned something that is not a version'], ['—'], ['unreadable', 'r'],
]

const VERSION_COLUMNS: readonly Column[] = [
  ['Version', 'm', '96px'],
  ['ID', 'm', '84px'],
  ['In force (UTC)', 'm', 'minmax(210px,1.5fr)'],
  ['State', 'c', '124px'],
  ['Platform', 'x', 'minmax(140px,1fr)'],
  ['Payer identity', 's', 'minmax(200px,1.4fr)'],
  ['Cur', 's', '96px'],
  ['Patterns', 'c', '124px'],
]

function readVersionRow(raw: unknown): RowRead {
  if (typeof raw !== 'object' || raw === null) {
    return {
      cells: UNREADABLE_VERSION_ROW,
      withheld: ['a version that did not arrive as an object'],
      bad: [],
    }
  }

  const row = raw as Partial<CatalogListItemView>
  const f = readFacts(row)
  const withheld = [...f.withheld]

  const state = versionStateChip(f.current, f.active, f.who)
  if (state.problem !== null) withheld.push(state.problem)

  const patterns = patternsChip(f.patterns, f.invalid, f.who, f.standing)
  if (patterns.problem !== null) withheld.push(patterns.problem)

  const window = effectiveWindow(row, f.current, f.who)
  withheld.push(...window.problems)

  return {
    cells: [
      f.version === null ? '—' : `v${String(f.version)}`,
      f.id === null ? '—' : String(f.id),
      window.text,
      state.cell,
      text(row.platform_id) === '' ? '—' : text(row.platform_id),
      identityCell(f.ibans, f.bic, f.aliases, f.patterns.items),
      currencyCell(row.currency),
      patterns.cell,
    ],
    withheld,
    bad: badPatternsOf(f),
  }
}

// ---------------------------------------------------------------------------
// Tables
// ---------------------------------------------------------------------------

/**
 * The footer, which is the only place the page's completeness is stated.
 *
 * This endpoint answers NO total — unlike the candidate queue next door, which
 * counts separately so a capped page can be recognised as one. Here a page is
 * just a page, so nothing may say "the catalog contains N entries", and a short
 * page is reported as a short page rather than as the whole catalog. Deducing
 * exhaustion from `LIMIT`/`OFFSET` semantics would be reasoning about the
 * backend's SQL, not about anything in the payload.
 */
function listFooter(shown: number, limit: number | null, offset: number | null): string {
  const from = offset === null || offset === 0 ? '' : ` · from offset ${String(offset)}`
  if (limit === null) {
    return `${String(shown)} entr(y/ies) on this page · the server echoed no page limit, and this read carries no total${from}`
  }
  if (shown >= limit) {
    return `${String(shown)} entr(y/ies) · the page is FULL at the server's limit of ${String(limit)}, so entries past it are not shown. This read carries no total${from}`
  }
  return `${String(shown)} entr(y/ies) · under the server's page limit of ${String(limit)}. This read carries no total, so the count is the page and not the catalog${from}`
}

function listTable(rows: readonly (readonly Cell[])[], limit: number | null, offset: number | null): TableBlock {
  return {
    t: 'table',
    title: 'payer_catalog_entries',
    // The console's three-value source vocabulary has no 'operational' member,
    // and 'ledger' is the word it already uses for our own Postgres. What the
    // badge is for is saying this is not the analytics mirror (law 12).
    src: 'ledger',
    note: 'The version in force for each entry — one row per entry_key, newest version. A version that governed last month is not on this list; it is in that entry’s history.',
    // The backend orders by `l.entry_key`, stated as the saved view because the
    // corpus's "Live · EUR" describes a filter this read does not apply.
    savedView: 'Entry key ascending',
    // No facet bar. The endpoint takes platform_id, status, limit and offset and
    // this read sends none of them, so a chip reading `currency EUR` would claim
    // a filter that is not applied — and the absence of a status filter is the
    // operationally important half of that.
    facets: false,
    cols: LIST_COLUMNS,
    rows,
    footer: listFooter(rows.length, limit, offset),
    foot2: 'unfiltered — no platform_id and no status are sent, so retired entries are in this list too',
  }
}

/**
 * Whether the timeline is the whole timeline, checked rather than asserted.
 *
 * The list already answered this: `versions` is `count(*)` over every row
 * sharing the key, and the history read returns every one of them, so the two
 * figures come from two queries and must agree. Stating "this is the whole
 * history" from the second alone was a claim resting on the backend's SQL
 * rather than on anything in the payload — and a short timeline is exactly the
 * failure that would make invariant 9's acceptance test silently unanswerable:
 * the April version simply would not be on screen, and nothing would say so.
 *
 * Nothing here is reconciled when the count could not be read. There is then
 * one figure and no second opinion, which is the position the list footer is
 * already careful about, and it is stated rather than glossed.
 */
function versionsFooter(entryKey: string, shown: number, expected: number | null): string {
  if (expected === null) {
    return `${String(shown)} version(s) of ${entryKey} · the list did not state how many versions this key has, so there is no second figure to check this timeline against and nothing here establishes it is complete`
  }
  if (expected === shown) {
    return `${String(shown)} version(s) of ${entryKey} · the list counted ${String(expected)} rows for this key and the timeline returned the same number, so the two reads agree this is the whole of it`
  }
  return `${String(shown)} version(s) of ${entryKey} · the list counted ${String(expected)} rows for this key, so the two reads DISAGREE and this timeline is not known to be complete. Catalog rows are never deleted, so the missing side is a read that lost versions rather than a history that shrank`
}

function versionsTable(
  entryKey: string, rows: readonly (readonly Cell[])[], expected: number | null,
): TableBlock {
  return {
    t: 'table',
    title: `payer_catalog_entries · history of ${entryKey}`,
    src: 'ledger',
    note: 'Every version of this one entry, newest first. State is read from current and active together — the status field is per-row and calls every superseded version "retired", which is true of the row and false of the entry.',
    savedView: 'Version descending',
    facets: false,
    cols: VERSION_COLUMNS,
    rows,
    footer: versionsFooter(entryKey, rows.length, expected),
    foot2: 'the operator who authored each version is stored as created_by and is not returned by this read',
  }
}

// ---------------------------------------------------------------------------
// Notes
// ---------------------------------------------------------------------------

/**
 * Invariant 9, on the screen rather than only in a document.
 *
 * Worth the vertical space because the shape of the list actively invites the
 * wrong reading. One row per entry, each with a version number and an effective
 * window, looks like a complete picture; it is one slice through a timeline, and
 * the slice is "now".
 */
const INVARIANT_NOTE: NoteBlock = {
  t: 'note',
  tone: 'v',
  kicker: 'Invariant 9',
  title: 'This list is the present tense of the catalog, not its history',
  body: 'Catalog rows are versioned and never updated in place, so that a recognition decision made in April can be reconstructed in October: the decision snapshots an entry id AND a version, and both rows still physically exist. The list read returns only the version in force for each entry — the row that governed in April is not in it. To answer "which version was in force on a date" for an entry, its history has to be read, and this screen reads exactly one.',
  ref: 'effective_from / effective_to · never an in-place edit · EditEntry closes v(n) and inserts v(n+1)',
}

/**
 * The one thing neither read can say, and it is the same gap `rc-cand` has.
 *
 * `recognition.Entry` carries an `Orchestrator` flag — the field that separates
 * `MASSPAY INC`, which identifies a payout ROUTE, from `FENIX INTERNATIONAL`,
 * which identifies a platform. `scanCatalogRow` does not select it and
 * `catalogEntryView` does not serialise it, so on this screen a route entry and
 * a platform entry are the same row with a different `platform_id`. The console
 * cannot mark them, so it says it cannot.
 */
const ORCHESTRATOR_NOTE: NoteBlock = {
  t: 'note',
  tone: 'a',
  kicker: 'Invariant 11b',
  title: 'This read cannot tell an orchestrator entry from a platform entry',
  body: 'A catalog entry whose platform_id names a payout route — via_masspay, via_payoneer — resolves a route and never a platform, because the same orchestrator legitimately means different platforms for different customers. The domain row carries a flag for this; neither of these two reads returns it, so a route entry renders here identically to a platform entry. Do not read a platform_id on this screen as proof that credits matching the entry are attributed to a platform.',
  ref: 'FR-3.3b · recognition.Entry.Orchestrator is not serialised by catalogEntryView',
}

/**
 * What the design draws here and these reads do not answer for.
 *
 * Named individually rather than as "some data is unavailable", because the
 * absences lead to different operator behaviour: no confidence ceiling means the
 * match strength has to be judged from the signals, and no author means an
 * accountability question has to go to the audit log rather than this screen.
 */
const NOT_SHOWN_NOTE: NoteBlock = {
  t: 'note',
  tone: 'a',
  kicker: 'Not shown',
  title: 'What the design draws here that these reads do not answer for',
  body: 'The confidence ceiling per entry · the operator who authored each version (created_by is stored and not returned) · the diff from one version to the next · the blast radius of a version, meaning how many credits it would have matched · and the version editor itself. The first four are fields with nothing behind them, so they are absent rather than blank. The editor is a write: its diff column is headed "Proposed", its date field "earliest permitted", and its button saves a new version — none of which is wired here, and all of which would have to be invented to draw.',
  ref: 'GET /catalog/entries + GET /catalog/entries/{entryKey}/versions are the whole of this screen',
}

function uniqueKeys(keys: readonly string[]): readonly string[] {
  return [...new Set(keys)].sort((a, b) => a.localeCompare(b))
}

/**
 * An entry that is live and inert.
 *
 * The loudest thing on this screen, and therefore the one whose scope has to be
 * exact. The backend deliberately does not fail the list over a pattern that
 * will not compile, because the row carrying it is the one an operator opened
 * this screen to find — an error would hide it behind a message naming a key
 * they then cannot look up. That arrangement only pays off if the console
 * shouts, and it stops paying off the moment the console shouts about entries
 * that are fine.
 *
 * Only versions with `current = true` reach here. Every claim in the body —
 * live, loads, never matches, the credit lands unrecognised — is present tense,
 * and none of it is true of a version something else superseded.
 */
function inertNote(keys: readonly string[]): NoteBlock {
  const unique = uniqueKeys(keys)
  return {
    t: 'note',
    tone: 'r',
    kicker: 'Live and inert',
    title: `${String(unique.length)} entr(y/ies) hold a remittance pattern that will not compile`,
    body: `${unique.join(' · ')} — each of these is live, loads without complaint, and can never match on the pattern it stores. Nothing else in the system reports this: recognition simply never fires, the credit lands unrecognised, and it looks like a payer nobody has catalogued yet. Fix the pattern by authoring a new version; the broken one stays in the history, as every version does.`,
    ref: 'invalid_patterns on the version in force',
  }
}

/**
 * A pattern that will not compile in a version nothing is matching against.
 *
 * Reported, and reported separately. Both reads surface these: the history
 * renders every superseded version, and the list renders a retired entry's
 * closed last one. They are not an incident — a superseded version stopped
 * matching when its successor opened — so they do not get the red note, and
 * folding them into it made a pattern somebody already fixed read as a live
 * failure of the entry that fixed it.
 *
 * They are still worth a line, because of what invariant 9 makes this screen
 * for: a decision taken while that version governed snapshotted it, and the
 * pattern did not fire then either. A credit that went unrecognised inside that
 * window has its explanation here rather than in a missing catalog entry.
 */
function dormantPatternNote(keys: readonly string[]): NoteBlock {
  const unique = uniqueKeys(keys)
  return {
    t: 'note',
    tone: 'a',
    kicker: 'Will not compile, and not in force',
    title: `${String(unique.length)} entr(y/ies) store a pattern that will not compile in a version that is not governing`,
    body: `${unique.join(' · ')} — the broken pattern is in a superseded or retired version, or in one whose standing this read could not establish. Nothing is failing to match on it today, so this is not the live-and-inert failure. It is named because a recognition decision taken while that version governed snapshotted it and the pattern did not fire then either: a credit that went unrecognised in that window is explained by this rather than by a payer nobody catalogued.`,
    ref: 'invalid_patterns on a version with current false or unread',
  }
}

/**
 * The page came back with no entries.
 *
 * Not called "the catalog is empty", because this read cannot establish that.
 * There is no total to agree with the page, so nought rows is what the page
 * said and not a count that confirms it — the same distinction `rc-cand` draws
 * between an empty page and an empty queue, minus the second opinion.
 */
const EMPTY_NOTE: NoteBlock = {
  t: 'note',
  tone: 'a',
  kicker: 'Nothing on this page',
  title: 'The catalog list returned no entries',
  body: 'The read succeeded and handed over nothing. This endpoint returns no total, so there is no second figure to confirm the catalog is genuinely empty — only that this page is. If it is empty, every inbound credit lands unrecognised until the first entry is promoted from the candidate queue, and no version history could be read because there was no entry to read one for.',
  ref: 'an empty entries array, with no total to check it against',
}

function withheldNote(withheld: readonly string[]): NoteBlock {
  return {
    t: 'note',
    tone: 'a',
    kicker: 'Partly unreadable',
    title: `${String(withheld.length)} thing(s) in this catalog could not be stated`,
    body: withheld.join(' · '),
    ref: 'A dash is not an empty set, and an unreadable row is still a row.',
  }
}

function historyNote(title: string, body: string, ref: string, tone: Tone = 'a'): NoteBlock {
  return { t: 'note', tone, kicker: 'Version history', title, body, ref }
}

// ---------------------------------------------------------------------------
// Choosing whose history to read
// ---------------------------------------------------------------------------

export interface HistoryPick {
  readonly key: string
  /** The entry's version count, when it could be stated exactly. */
  readonly versions: number | null
}

/**
 * Which entry's timeline this screen reads.
 *
 * The loader gets one screen and the endpoint takes one key, so a choice has to
 * be made and it has to be the same one every time — a timeline that changes
 * subject between refreshes is worse than none.
 *
 * The deepest history wins, ties broken by list order, which the backend sorts
 * by `entry_key` ascending. The entry with the most versions is the one where
 * "which version was in force then" is a real question; an entry with one
 * version has no timeline to disagree about. Nothing is synthesised — the key
 * comes off the payload, and a page with no usable key produces no read rather
 * than a guess at one.
 */
export function pickHistoryKey(entries: readonly unknown[]): HistoryPick | null {
  let best: HistoryPick | null = null
  for (const raw of entries) {
    if (typeof raw !== 'object' || raw === null) continue
    const row = raw as Partial<CatalogListItemView>
    const key = text(row.entry_key)
    if (key === '') continue

    const versions = exactInteger(row.versions)
    if (best === null) {
      best = { key, versions }
      continue
    }
    // An unreadable count never displaces a readable one: the point of the
    // choice is depth, and "we could not count" is not deeper than 4.
    if (versions !== null && (best.versions === null || versions > best.versions)) {
      best = { key, versions }
    }
  }
  return best
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

/** One read, resolved into an answer either way. */
export type Attempt =
  | { readonly ok: true; readonly payload: unknown }
  | {
      readonly ok: false
      readonly reason: string
      readonly decisionRef: string | null
      /**
       * The role the backend said this read needs, on an RBAC refusal.
       *
       * `adminError.RequiredRole` is populated on every permission refusal
       * (`internal/server/admin.go`) precisely so the console does not hold a
       * second copy of the permission model. `null` means the refusal genuinely
       * named none — an authz refusal is about risk rather than role scope and
       * carries a `decision_ref` instead.
       */
      readonly requiredRole: string | null
      readonly refused: boolean
    }

/**
 * How a refusal is quoted, and what is said when it cannot be.
 *
 * Law 13 — every refusal surfaces a `decision_ref`. A refusal that arrives
 * without one leaves an operator with nothing to put in an escalation, and the
 * console says exactly that rather than inventing a reference: an invented ref
 * resolves to nothing in the decision explorer and costs a support agent the
 * call.
 *
 * But "nothing to quote" is a claim about the payload, so the payload has to be
 * read before it is made. The overwhelmingly likely 403 on a read-only screen
 * is the RBAC one, which carries no `decision_ref` and DOES name the role —
 * that is the whole reason the backend sends `required_role`. Saying nothing
 * was quotable while dropping it is the same bug the denied panel shipped: a
 * statement about a field nobody looked at, on the one line an operator reads
 * to find out who to ask.
 */
function refLine(failed: Extract<Attempt, { ok: false }>, fallback: string): string {
  if (failed.decisionRef !== null) return `decision_ref ${failed.decisionRef}`
  if (failed.requiredRole !== null) {
    return `No decision_ref was sent. The backend named what this read requires: ${failed.requiredRole}.`
  }
  if (failed.refused) {
    return 'The refusal arrived without a decision_ref and named no required role, so there is nothing to quote in an escalation.'
  }
  return fallback
}

function isCatalogList(payload: unknown): payload is CatalogListResponse {
  if (typeof payload !== 'object' || payload === null) return false
  return Array.isArray((payload as Partial<CatalogListResponse>).entries)
}

function isCatalogVersions(payload: unknown): payload is CatalogVersionsResponse {
  if (typeof payload !== 'object' || payload === null) return false
  return Array.isArray((payload as Partial<CatalogVersionsResponse>).versions)
}

// ---------------------------------------------------------------------------
// Composition
// ---------------------------------------------------------------------------

interface Rendered {
  readonly blocks: readonly Block[]
  /**
   * Row-level problems, which belong on the SCREEN and not only in the chip's
   * tooltip. A history row that rendered a dash is a dash an operator is owed
   * an explanation for, exactly as a list row is; routing these to provenance
   * alone left a clean list with an unreadable timeline showing no note at all.
   */
  readonly withheld: readonly string[]
  /** Provenance-only lines: what of the timeline is absent, and why. */
  readonly missing: readonly string[]
  readonly bad: readonly BadPattern[]
  /** Whether a timeline was actually drawn. */
  readonly rendered: boolean
}

/** The shape of every branch that produces no timeline. */
function noTimeline(blocks: readonly Block[], missing: readonly string[]): Rendered {
  return { blocks, withheld: [], missing, bad: [], rendered: false }
}

/**
 * The version history section, or the reason there is not one.
 *
 * Every branch produces something visible. A missing timeline that renders as
 * nothing leaves the list looking like the whole screen, and the list is the one
 * thing on it that cannot answer invariant 9's question.
 */
function renderHistory(pick: HistoryPick | null, history: Attempt | null, entryCount: number): Rendered {
  if (pick === null) {
    if (entryCount === 0) {
      // The empty note already says it, and repeating it here would put two
      // notes on an empty screen saying the same thing twice.
      return noTimeline([], ['the version history of any entry (no entry was returned to read one for)'])
    }
    return noTimeline(
      [historyNote(
        'No version history could be requested',
        'Entries came back, and none of them carried an entry_key this console could use — the history endpoint is addressed by that key and there was nothing to address it with. So no timeline is shown for any entry, and this screen cannot answer which version was in force on a past date.',
        'GET /catalog/entries/{entryKey}/versions needs a key the list did not supply',
        'r',
      )],
      ['the version history of every entry (no row carried a usable entry_key)'],
    )
  }

  if (history === null) {
    return noTimeline(
      [historyNote(
        `The history of ${pick.key} was not read`,
        'The list named an entry to read a timeline for and no history read was made, so nothing on this screen says which version of it governed on any past date.',
        'no second read was attempted',
        'r',
      )],
      [`the version history of ${pick.key} (not read)`],
    )
  }

  if (!history.ok) {
    return noTimeline(
      [historyNote(
        `The history of ${pick.key} could not be read`,
        `${history.reason} The list above is unaffected and is still the version in force for each entry — but with no timeline, this screen cannot say which version governed on any earlier date, for this entry or any other.`,
        refLine(history, 'Nothing of this entry’s timeline was read.'),
        'r',
      )],
      [`the version history of ${pick.key} (could not be read)`],
    )
  }

  if (!isCatalogVersions(history.payload)) {
    return noTimeline(
      [historyNote(
        `The history of ${pick.key} returned a shape this console does not know`,
        'The read succeeded and what came back is not a version list, so none of it is rendered. An unknown shape is not an empty history: an entry with no versions and an entry whose versions could not be understood are different answers and only one of them is safe to act on.',
        'the payload carried no versions array',
        'r',
      )],
      [`the version history of ${pick.key} (unknown shape)`],
    )
  }

  const payload = history.payload
  const answered = text(payload.entry_key)
  if (answered !== pick.key) {
    // Never rendered as this entry's timeline on the strength of having asked
    // for it. The rows may be genuine and belong to something else, and a
    // history labelled with the wrong key is how the wrong version gets blamed.
    return noTimeline(
      [historyNote(
        `The history read answered for a different entry`,
        `The timeline of ${pick.key} was requested and the response names ${answered === '' ? 'no entry at all' : `"${answered}"`}. Its rows are not shown under either name: attributing a version window to the wrong entry is the precise failure invariant 9 exists to prevent.`,
        'entry_key in the response does not match the one requested',
        'r',
      )],
      [`the version history of ${pick.key} (the response named a different entry)`],
    )
  }

  const reads = payload.versions.map(readVersionRow)
  if (reads.length === 0) {
    // The key came off the list, so the entry exists and has at least the one
    // version that was on it. A history of nought contradicts that, and the
    // honest reading is that the timeline is wrong rather than that the entry
    // materialised without one.
    return noTimeline(
      [historyNote(
        `The history of ${pick.key} came back empty`,
        'This key was taken from the list above, so at least one version of it exists — the row is right there. An empty history contradicts that, and the endpoint answers 200 with an empty list rather than 404, so this is not a mistyped key. Something between the two reads disagrees, and the timeline is the side that came back with nothing.',
        'a version list of zero for a key the list returned',
        'r',
      )],
      [`the version history of ${pick.key} (the endpoint returned no versions for an entry the list did return)`],
    )
  }

  const blocks: Block[] = [versionsTable(pick.key, reads.map((r) => r.cells), pick.versions)]

  // The two reads counted the same rows and disagreed. Said in the provenance
  // as well as in the footer, because a timeline missing a version answers
  // invariant 9's question wrongly rather than refusing to: the operator gets a
  // window that looks continuous with a version silently absent from it.
  const missing: string[] = []
  if (pick.versions !== null && pick.versions !== reads.length) {
    missing.push(
      `version(s) of ${pick.key} that one read has and the other does not — the list counted ${String(pick.versions)} and the timeline returned ${String(reads.length)}, so this timeline is not known to be complete`,
    )
  }

  return {
    blocks,
    withheld: reads.flatMap((r) => r.withheld),
    missing,
    bad: reads.flatMap((r) => r.bad),
    rendered: true,
  }
}

/**
 * The screen when the list itself could not be read.
 *
 * One note and nothing else. Not an empty table — an empty catalog table reads
 * as an empty catalog, and "no payer is catalogued" is a sentence an operator
 * acts on by promoting candidates. And never the design corpus, whose six
 * invented entries name real companies against invented IBANs.
 *
 * The provenance is `unread`, not `partial`: nothing on the screen came from
 * anywhere, and the chip renders one word. `Partial` says some of this is real.
 */
function unreadableScreen(failed: Extract<Attempt, { ok: false }>): ScreenData {
  return {
    blocks: [{
      t: 'note',
      tone: 'r',
      kicker: 'Payer catalog',
      title: 'This screen could not be read',
      body: `${failed.reason} No entry, version, effective window or pattern below is known — that is different from the catalog being empty, and different again from every entry being healthy. No version history was read either: the history endpoint is addressed by an entry key, and the list is where the keys come from.`,
      ref: refLine(failed, 'Nothing on this screen was read.'),
    }],
    provenance: { kind: 'unread', source: SOURCE, reason: failed.reason },
  }
}

/**
 * Folds both reads into the blocks the screen renders.
 *
 * Pure, and separate from the fetch, so every absence path — including the one
 * that matters most, a list that read and a timeline that did not — is
 * table-testable without a client.
 *
 * `at` is when the console received the answers. It is a parameter because a
 * pure function does not read a clock, and it is labelled a receipt on screen
 * because neither endpoint states when it read its rows.
 */
export function composeCatalog(list: Attempt, history: Attempt | null, at: Date): ScreenData {
  if (!list.ok) return unreadableScreen(list)

  if (!isCatalogList(list.payload)) {
    return unreadableScreen({
      ok: false,
      reason: 'The catalog list returned a shape this console does not know.',
      decisionRef: null,
      requiredRole: null,
      refused: false,
    })
  }

  const payload = list.payload
  const reads = payload.entries.map(readListRow)
  const rows = reads.map((r) => r.cells)

  // A page limit below 1 is not a page limit. The handler caps and floors it,
  // so it should never arrive — but the footer compares `shown >= limit` to
  // decide whether entries are being cut off, and a zero would report a
  // three-row page as full at the server's limit of nought. Withheld rather
  // than repaired, for the same reason the offset below is.
  const limitRaw = exactInteger(payload.limit)
  const limit = limitRaw !== null && limitRaw >= 1 ? limitRaw : null
  const offsetRaw = exactInteger(payload.offset)
  // A negative offset is not an offset. Withheld rather than clamped to zero,
  // because zero is the one value that means "from the start".
  const offset = offsetRaw !== null && offsetRaw >= 0 ? offsetRaw : null

  const pick = pickHistoryKey(payload.entries)
  const timeline = renderHistory(pick, history, payload.entries.length)

  const blocks: Block[] = [listTable(rows, limit, offset)]
  if (rows.length === 0) blocks.push(EMPTY_NOTE)
  blocks.push(...timeline.blocks, INVARIANT_NOTE, ORCHESTRATOR_NOTE, NOT_SHOWN_NOTE)

  // Split by standing, not merged by key. An entry whose version in force is
  // broken belongs in the red note even if older versions are broken too, so
  // the in-force keys claim their entries first and the dormant note takes only
  // what is left — otherwise one entry would be named in both and read as two
  // separate problems.
  const bad = [...reads.flatMap((r) => r.bad), ...timeline.bad]
  const inForce = bad.filter((b) => b.standing === 'in-force').map((b) => b.key)
  const claimed = new Set(inForce)
  const dormant = bad
    .filter((b) => b.standing !== 'in-force' && !claimed.has(b.key))
    .map((b) => b.key)

  if (inForce.length > 0) blocks.push(inertNote(inForce))
  if (dormant.length > 0) blocks.push(dormantPatternNote(dormant))

  // Both reads' row problems, in one note. The history's used to reach the
  // provenance tooltip and nothing else, so a clean list beside an unreadable
  // timeline drew dashes on screen with no note to explain any of them.
  const withheld = [...reads.flatMap((r) => r.withheld), ...timeline.withheld]
  if (withheld.length > 0) blocks.push(withheldNote(withheld))

  // # Why this screen is permanently `partial` and never `live`
  //
  // `live` means everything on the screen was read. Five things the design draws
  // are answered by neither endpoint on the happiest possible read, and one of
  // them — the authoring operator — is the field somebody comes to this screen
  // for after an incident. Badging it `Live` once the rows parse cleanly would
  // claim the screen is complete; it is complete about the present tense of the
  // catalog and silent about four other questions.
  //
  // The sixth line is structural rather than a gap in the payload: this screen
  // reads ONE timeline, so every other entry's history is unread by design.
  const missing: string[] = [
    'the confidence ceiling of each entry (neither read carries one)',
    'the operator who authored each version (created_by is stored and returned by neither read)',
    'the diff from one version to the next (neither read carries one)',
    'the blast radius of a version — how many credits it would have matched (neither read carries one)',
    'whether an entry is an orchestrator route rather than a platform (invariant 11b — the flag exists in the domain row and is not serialised)',
    'how many entries the catalog holds in total (this endpoint pages and does not count)',
  ]
  if (pick !== null && payload.entries.length > 1) {
    // "Except X" only holds if X's timeline is actually on screen. When the
    // history read produced nothing, saying it would imply one entry WAS
    // answered for and quietly drop the other entries from the account.
    const others = String(payload.entries.length - 1)
    missing.push(timeline.rendered
      ? `the version history of every entry except ${pick.key} (${others} other entr(y/ies) on this page — this screen reads one timeline)`
      : `the version history of the other ${others} entr(y/ies) on this page (this screen reads one timeline, and that read produced none)`)
  }
  if (limit === null) missing.push('the page limit the server applied (it was not echoed as a positive integer)')
  if (offset === null) missing.push('the page offset the server applied (it was not echoed as a non-negative integer)')
  missing.push(...timeline.missing, ...withheld)

  const provenance: DataSource = {
    kind: 'partial',
    source: SOURCE,
    authority: 'operational',
    readAt: `${readAtStamp(at)} (console receipt — neither catalog read states when it read)`,
    missing,
  }

  return { blocks, provenance }
}

// ---------------------------------------------------------------------------
// Loading
// ---------------------------------------------------------------------------

/**
 * One read, resolved into an answer either way.
 *
 * An `AbortError` is rethrown: a navigation away is a cancellation, not a
 * degraded screen, and swallowing it would render "the catalog could not be
 * read" over a screen the operator has already left.
 */
async function attempt(read: () => Promise<unknown>, subject: string): Promise<Attempt> {
  try {
    return { ok: true, payload: await read() }
  } catch (error) {
    if (error instanceof ApiError) {
      return {
        ok: false,
        reason: `${subject} answered ${String(error.status)}${error.code === null ? '' : ` (${error.code})`}.`,
        decisionRef: error.decisionRef,
        requiredRole: error.requiredRole,
        refused: error.isForbidden,
      }
    }
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    return {
      ok: false,
      reason: `${subject} could not be reached.`,
      decisionRef: null,
      requiredRole: null,
      refused: false,
    }
  }
}

/**
 * Reads the catalog and one entry's version history.
 *
 * SEQUENTIAL, and it has to be: the history endpoint is addressed by an entry
 * key and the list is the only place a key comes from. Nothing is guessed to
 * make the two parallel — an invented key would 200 with an empty history and
 * render as an entry with no versions.
 *
 * No query parameters are sent. `status` is omitted so retired entries are in
 * the list, which is what makes "does this key already exist" answerable before
 * a promotion; `limit` is omitted so the server applies its own cap, which is
 * the largest page it will give.
 *
 * Read-only, and structurally so. Create, edit, retire and promote are audited
 * versioned writes and are not wired here.
 */
export const loadCatalog: ScreenLoader = async (api) => {
  const list = await attempt(() => api.get<unknown>(CATALOG_ENTRIES_PATH), 'The payer catalog')
  if (!list.ok) return composeCatalog(list, null, new Date())

  const pick = isCatalogList(list.payload) ? pickHistoryKey(list.payload.entries) : null
  const history = pick === null
    ? null
    : await attempt(
        () => api.get<unknown>(catalogVersionsPath(pick.key)),
        `The version history of ${pick.key}`,
      )

  return composeCatalog(list, history, new Date())
}
